import { Container, Graphics, Text } from "pixi.js";
import { getComponent } from "../world/Entity";
import type { EntityId } from "../world/Entity";
import type { World } from "../world/World";

export function isDebugEnabled(): boolean {
  if (typeof location === "undefined") return false;
  return new URLSearchParams(location.search).get("debug") === "true";
}

export interface DebugStatus {
  readonly fps: number;
  readonly sceneId: string;
  readonly entityCount: number;
  readonly selectedEntityId?: EntityId;
  readonly saveStatus: "idle" | "saving" | "saved" | "error";
}

/** FPS/scene/entity-count/selection/save-status readout + surface & selection outlines. `?debug=true` gated. */
export class DebugOverlay {
  readonly container = new Container();
  private readonly text: Text;
  private readonly outlines = new Graphics();

  constructor() {
    this.text = new Text({ text: "", style: { fill: 0x00ff88, fontSize: 12, fontFamily: "monospace" } });
    this.text.position.set(8, 8);
    this.container.addChild(this.outlines, this.text);
    this.container.zIndex = 10_000;
  }

  update(status: DebugStatus, world: World): void {
    this.text.text = [
      `fps: ${status.fps.toFixed(0)}`,
      `scene: ${status.sceneId}`,
      `entities: ${status.entityCount}`,
      `selected: ${status.selectedEntityId ?? "-"}`,
      `save: ${status.saveStatus}`,
    ].join("\n");

    this.outlines.clear();
    const scene = world.getScene(status.sceneId);
    if (!scene) return;
    for (const entity of scene.entities) {
      const surface = getComponent(entity, "surface");
      if (surface) {
        this.outlines
          .rect(
            entity.transform.position.x + surface.bounds.x,
            entity.transform.position.y + surface.bounds.y,
            surface.bounds.width,
            surface.bounds.height,
          )
          .stroke({ color: 0x00ffff, width: 1 });
      }
      if (entity.id === status.selectedEntityId) {
        this.outlines.rect(entity.transform.position.x - 32, entity.transform.position.y - 64, 64, 64).stroke({ color: 0xffff00, width: 2 });
      }
    }
  }
}
